import { useCallback, useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useAgentStore } from '@/stores/agentStore';
import { useAuthStore } from '@/stores/authStore';
import type {
  AgentEvent,
  DocumentUpdateEvent,
  DocumentCompletionEvent,
  GapDetectedEvent,
  FieldSuggestionEvent,
  FinalSummaryEvent,
  MeetingEndingEvent,
  ProcessingStatusEvent,
  ErrorEvent,
} from '@/types/agent.types';

interface UseAgentWebSocketProps {
  roomCode: string;
  enabled?: boolean;
}

const RECONNECT_DELAY = 3000;
const PING_INTERVAL = 30000;

export const useAgentWebSocket = ({
  roomCode,
  enabled = true,
}: UseAgentWebSocketProps) => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);

  const accessToken = useAuthStore((state) => state.accessToken);
  const {
    connectionState,
    setConnectionState,
    updateDocumentField,
    setDocumentCompletion,
    addGapWithAggregation,
    addSuggestion,
    setSummary,
    setError,
    setIsEndingMeeting,
    setMeetingEndingCountdown,
    setProcessingStatus,
    clearAll,
  } = useAgentStore();

  // Get WebSocket URL (without token in URL for security)
  const getWsUrl = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${protocol}//${host}/ws/agent/${roomCode.toUpperCase()}`;
  }, [roomCode]);

  // Handle incoming messages
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const data: AgentEvent = JSON.parse(event.data);
        console.log('[Agent WS] Received:', data.type, data);

        switch (data.type) {
          case 'auth_required':
            // Server requests authentication - send token via message (more secure than URL)
            if (wsRef.current?.readyState === WebSocket.OPEN && accessToken) {
              wsRef.current.send(JSON.stringify({ type: 'auth', token: accessToken }));
              console.log('[Agent WS] Sent auth token');
            }
            break;

          case 'connected':
            setConnectionState('connected');
            notifications.show({
              title: 'Copilot conectado',
              message: 'O assistente de requisitos está ativo',
              color: 'green',
            });
            break;

          case 'field_suggestion': {
            const suggestionEvent = data as FieldSuggestionEvent;
            const suggestion = suggestionEvent.data;

            // Extrair e normalizar valores
            const proposedValue = suggestion.proposedValue?.trim() || '';
            const currentValue = suggestion.currentValue?.trim() || '';

            // Se o valor proposto é vazio/null, ignorar completamente
            if (!proposedValue) {
              console.log('[Agent WS] Skipping - empty proposed value:', suggestion.fieldName);
              break;
            }

            // Obter sugestão pendente atual do store
            const currentPendingSuggestions = useAgentStore.getState().pendingSuggestions;
            const existingPendingSuggestion = currentPendingSuggestions.find(
              (s) => s.fieldName === suggestion.fieldName
            );
            const existingPendingValue = existingPendingSuggestion?.proposedValue?.trim() || '';

            // Verificar se campo já tem valor aprovado no store (documentFields)
            const documentFields = useAgentStore.getState().documentFields;
            const approvedField = documentFields.find(
              (f) => f.field_name === suggestion.fieldName
            );
            const approvedValue = approvedField?.value?.trim() || '';

            // Determinar valor de referência para comparação:
            // 1. Sugestão pendente (prioridade máxima)
            // 2. Valor aprovado do store (campo já preenchido)
            // 3. currentValue do backend (fallback)
            const referenceValue = existingPendingValue || approvedValue || currentValue;
            const hasReference = referenceValue.length > 0;

            // Se valores são iguais, não mostrar
            if (hasReference && proposedValue.toLowerCase() === referenceValue.toLowerCase()) {
              console.log('[Agent WS] Skipping notification - same value:', suggestion.fieldName);
              break;
            }

            // Comparar contagem de palavras contra a referência
            if (hasReference) {
              const referenceWords = referenceValue.split(/\s+/).length;
              const proposedWords = proposedValue.split(/\s+/).length;

              // Se a nova sugestão não tem mais informação, não mostrar
              if (proposedWords <= referenceWords) {
                console.log('[Agent WS] Skipping notification - not more complete:', suggestion.fieldName);
                break;
              }
            }

            // Adicionar/atualizar sugestão no store
            addSuggestion(suggestion);

            // Mostrar notificação com valor completo (sem truncamento)
            const isUpdate = hasReference;
            const displayName = suggestion.displayName || suggestion.fieldName;

            notifications.show({
              title: isUpdate ? 'Sugestão atualizada' : 'Nova sugestão',
              message: `${displayName}: "${proposedValue}"`,
              color: isUpdate ? 'yellow' : 'blue',
              autoClose: 5000,
            });
            break;
          }

          case 'document_update': {
            const docEvent = data as DocumentUpdateEvent;
            updateDocumentField({
              field_id: docEvent.data.field_id,
              field_name: docEvent.data.field_name,
              value: docEvent.data.value,
              confidence: docEvent.data.confidence,
              extracted_at: new Date().toISOString(),
            });
            break;
          }

          case 'document_completion': {
            const completionEvent = data as DocumentCompletionEvent;
            setDocumentCompletion(completionEvent.data);
            break;
          }

          case 'gap_detected': {
            const gapEvent = data as GapDetectedEvent;
            // Respect the resolved status from backend
            // Gaps for already filled fields will have resolved=true
            addGapWithAggregation({
              id: gapEvent.data.id,
              field_name: gapEvent.data.field_name,
              section: gapEvent.data.section,
              description: gapEvent.data.description,
              priority: gapEvent.data.priority,
              suggested_questions: gapEvent.data.suggested_questions,
              resolved: gapEvent.data.resolved ?? false,
            });
            break;
          }

          case 'final_summary': {
            const summaryEvent = data as FinalSummaryEvent;
            setSummary(summaryEvent.data);
            setIsEndingMeeting(false); // Reset ending state after receiving summary
            notifications.show({
              title: 'Resumo gerado',
              message: 'O resumo da reunião está disponível',
              color: 'blue',
            });
            break;
          }

          case 'meeting_ending': {
            // 5 second warning before meeting ends
            const endingEvent = data as MeetingEndingEvent;
            setMeetingEndingCountdown(endingEvent.data.countdown);
            console.log('[Agent WS] Meeting ending in', endingEvent.data.countdown, 'seconds');
            break;
          }

          case 'meeting_ended':
            setIsEndingMeeting(false); // Reset ending state
            setMeetingEndingCountdown(null); // Clear countdown
            notifications.show({
              title: 'Reunião encerrada',
              message: 'O agente finalizou a análise',
              color: 'gray',
            });
            break;

          case 'processing_status': {
            const statusEvent = data as ProcessingStatusEvent;
            setProcessingStatus(
              statusEvent.data.node,
              statusEvent.data.status === 'started'
            );
            break;
          }

          case 'error': {
            const errorEvent = data as ErrorEvent;
            setError(errorEvent.message);
            console.error('[Agent WS] Error:', errorEvent.message);
            break;
          }

          case 'pong':
            // Keep-alive response, ignore
            break;

          case 'analysis_complete':
            // Analysis of current chunk completed - no UI action needed
            console.log('[Agent WS] Chunk analysis completed');
            break;

          default:
            console.log('[Agent WS] Unknown event type:', data.type);
        }
      } catch (e) {
        console.error('[Agent WS] Failed to parse message:', e);
      }
    },
    [
      accessToken,
      updateDocumentField,
      setDocumentCompletion,
      addGapWithAggregation,
      addSuggestion,
      setSummary,
      setError,
      setIsEndingMeeting,
      setMeetingEndingCountdown,
      setProcessingStatus,
      setConnectionState,
    ]
  );

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!enabled || !accessToken || isConnecting || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    setIsConnecting(true);
    setConnectionState('connecting');

    try {
      const url = getWsUrl();
      console.log('[Agent WS] Connecting to:', url);

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[Agent WS] Connected');
        setIsConnecting(false);

        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_INTERVAL);
      };

      ws.onmessage = handleMessage;

      ws.onerror = (error) => {
        console.error('[Agent WS] Error:', error);
        setError('Erro na conexão com o agente');
      };

      ws.onclose = (event) => {
        console.log('[Agent WS] Closed:', event.code, event.reason);
        setIsConnecting(false);
        setConnectionState('disconnected');

        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Don't auto-reconnect - let the useEffect handle reconnection based on enabled state
        // This prevents reconnect loops when the component is unmounting or conditions change
      };
    } catch (e) {
      console.error('[Agent WS] Connection error:', e);
      setIsConnecting(false);
      setConnectionState('error');
    }
  }, [enabled, accessToken, getWsUrl, handleMessage, setConnectionState, setError, isConnecting]);

  // Disconnect
  const disconnect = useCallback(() => {
    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Clear ping interval
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }

    setConnectionState('disconnected');
  }, [setConnectionState]);

  // Send transcription chunk
  const sendChunk = useCallback((text: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'chunk', text }));
      console.log('[Agent WS] Sent chunk:', text.substring(0, 50) + '...');
    }
  }, []);

  // End meeting and request summary
  const endMeeting = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      setIsEndingMeeting(true); // Mark as ending before sending
      wsRef.current.send(JSON.stringify({ type: 'end_meeting' }));
      console.log('[Agent WS] Sent end_meeting');
    }
  }, [setIsEndingMeeting]);

  // Connect on mount and when dependencies change
  useEffect(() => {
    // Only connect if all conditions are met
    if (enabled && accessToken && roomCode) {
      connect();
    } else {
      // Disconnect if conditions are no longer met
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, accessToken, roomCode]); // Removed connect/disconnect from deps to avoid loops

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAll();
    };
  }, [clearAll]);

  return {
    connectionState,
    isConnected: connectionState === 'connected',
    connect,
    disconnect,
    sendChunk,
    endMeeting,
  };
};
